import { useCallback, useMemo, useState } from 'react';
import { getUsageCostRange } from './api';
import type {
  UsageClinicRow,
  UsageCostFilterState,
  UsageCostFilters,
  UsageRangePreset,
  UsageTrendMetric,
  UsageTrendView,
  UsageUserRow,
} from './types';

const QUERY_KEYS = [
  'start_date',
  'end_date',
  'clinic_id',
  'user_id',
  'feature',
  'provider',
  'model',
  'trend_group',
  'trend_metric',
] as const;

const parseNumber = (value: string | null) => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeTrendGroup = (value: string | null): UsageTrendView => {
  if (value === 'clinic' || value === 'user' || value === 'overall') return value;
  return 'overall';
};

const normalizeTrendMetric = (value: string | null): UsageTrendMetric => {
  if (value === 'cost' || value === 'cost_per_audio_minute' || value === 'events' || value === 'data_gaps')
    return value;
  return 'cost';
};

const readInitialState = (): UsageCostFilterState => {
  const defaults = getUsageCostRange('30d');
  const params = new URLSearchParams(window.location.search);
  const startDate = params.get('start_date') || defaults.startDate;
  const endDate = params.get('end_date') || defaults.endDate;
  const rangePreset = startDate === defaults.startDate && endDate === defaults.endDate ? '30d' : 'custom';

  return {
    rangePreset,
    startDate,
    endDate,
    clinicId: parseNumber(params.get('clinic_id')),
    userId: parseNumber(params.get('user_id')),
    feature: params.get('feature'),
    provider: params.get('provider'),
    model: params.get('model'),
    trendGroup: normalizeTrendGroup(params.get('trend_group')),
    trendMetric: normalizeTrendMetric(params.get('trend_metric')),
  };
};

const writeUrl = (state: UsageCostFilterState) => {
  const params = new URLSearchParams(window.location.search);
  for (const key of QUERY_KEYS) params.delete(key);

  params.set('start_date', state.startDate);
  params.set('end_date', state.endDate);
  if (state.clinicId != null) params.set('clinic_id', String(state.clinicId));
  if (state.userId != null) params.set('user_id', String(state.userId));
  if (state.feature) params.set('feature', state.feature);
  if (state.provider) params.set('provider', state.provider);
  if (state.model) params.set('model', state.model);
  if (state.trendGroup !== 'overall') params.set('trend_group', state.trendGroup);
  if (state.trendMetric !== 'cost') params.set('trend_metric', state.trendMetric);

  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
};

export const useUsageCostFilters = () => {
  const [state, setState] = useState(readInitialState);

  const updateState = useCallback((patch: Partial<UsageCostFilterState>) => {
    setState((current) => {
      const next = { ...current, ...patch };
      writeUrl(next);
      return next;
    });
  }, []);

  const setRangePreset = useCallback(
    (rangePreset: UsageRangePreset) => {
      if (rangePreset === 'custom') {
        updateState({ rangePreset });
        return;
      }
      updateState({ rangePreset, ...getUsageCostRange(rangePreset) });
    },
    [updateState]
  );

  const setDate = useCallback(
    (patch: Pick<Partial<UsageCostFilters>, 'startDate' | 'endDate'>) => {
      updateState({ ...patch, rangePreset: 'custom' });
    },
    [updateState]
  );

  const setTrendGroup = useCallback((trendGroup: UsageTrendView) => updateState({ trendGroup }), [updateState]);
  const setTrendMetric = useCallback((trendMetric: UsageTrendMetric) => updateState({ trendMetric }), [updateState]);
  const setFilter = useCallback(
    (patch: Pick<Partial<UsageCostFilters>, 'clinicId' | 'userId' | 'feature' | 'provider' | 'model'>) =>
      updateState(patch),
    [updateState]
  );

  const clearFilters = useCallback(
    () => updateState({ clinicId: null, userId: null, feature: null, provider: null, model: null }),
    [updateState]
  );

  const setClinicFilter = useCallback(
    (clinic: UsageClinicRow) => updateState({ clinicId: clinic.clinic_id, userId: null }),
    [updateState]
  );

  const setUserFilter = useCallback((user: UsageUserRow) => updateState({ userId: user.user_id }), [updateState]);

  const filters = useMemo<UsageCostFilters>(
    () => ({
      startDate: state.startDate,
      endDate: state.endDate,
      clinicId: state.clinicId,
      userId: state.userId,
      feature: state.feature,
      provider: state.provider,
      model: state.model,
    }),
    [state]
  );

  return {
    state,
    filters,
    setRangePreset,
    setDate,
    setFilter,
    setTrendGroup,
    setTrendMetric,
    clearFilters,
    setClinicFilter,
    setUserFilter,
  };
};
